import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, StatCard, StatusPill, Skeleton, EmptyState } from './shared';

interface Campaign {
  id: string;
  name: string;
  source_name: string;
  source_id: string | null;
  campaign_type: string;
  cost_model: string;
  cost_value: number;
  cpa_event: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  aff_id: string | null;
  postback_override: string | null;
  is_active: boolean;
}

interface CampaignStats {
  clicks: number;
  signups: number;
  first_survey_completes: number;
  total_cost: number;
  avg_cac: number;
  members_with_redemptions: number;
  total_member_earnings: number;
  roi: number;
  ltv_to_cac: number;
}

interface FunnelStep {
  step: string;
  count: number;
  rate?: number;
}

interface ClickRow {
  id: string;
  session_id: string;
  aff_id: string | null;
  sub1: string | null;
  sub2: string | null;
  created_at: string;
  member: { email: string } | null;
}

interface AdvertiserSummary {
  postback_configs: Array<{ id: string; name: string; event: string; url_template: string; is_active: boolean }>;
  global_postback_configs: Array<{ id: string; name: string; event: string; url_template: string; is_active: boolean }>;
}

export default function CampaignDetail({ campaignId }: { campaignId: string }) {
  const statsQ = useQuery({
    queryKey: ['admin', 'ua', 'campaign', campaignId, 'stats'],
    queryFn: () => apiFetch(`/api/ua/admin/campaigns/${campaignId}/stats`) as Promise<{ campaign: Campaign; stats: CampaignStats }>,
  });
  const funnelQ = useQuery({
    queryKey: ['admin', 'ua', 'campaign', campaignId, 'funnel'],
    queryFn: () => apiFetch(`/api/ua/admin/campaigns/${campaignId}/funnel`) as Promise<{ funnel: FunnelStep[] }>,
  });
  const [page, setPage] = useState(1);
  const clicksQ = useQuery({
    queryKey: ['admin', 'ua', 'campaign', campaignId, 'clicks', page],
    queryFn: () => apiFetch(`/api/ua/admin/campaigns/${campaignId}/clicks?page=${page}`) as Promise<{ clicks: ClickRow[]; total: number; perPage: number }>,
  });

  const campaign = statsQ.data?.campaign;
  const stats = statsQ.data?.stats;
  const funnel = funnelQ.data?.funnel ?? [];

  // Load advertiser-level postback configs so we can answer "which postbacks
  // fire for this campaign". The campaign matches on source_id → advertiser,
  // whose postback configs are keyed on source string = advertiser name.
  const advertiserQ = useQuery({
    queryKey: ['admin', 'ua', 'advertiser', campaign?.source_id ?? null],
    queryFn: () => campaign?.source_id
      ? apiFetch(`/api/ua/admin/advertisers/${campaign.source_id}`) as Promise<AdvertiserSummary>
      : Promise.resolve({ postback_configs: [], global_postback_configs: [] } as AdvertiserSummary),
    enabled: !!campaign,
  });

  if (statsQ.isLoading) return <Skeleton className="h-96" />;
  if (!campaign || !stats) return <EmptyState title="Campaign not found" />;

  const totalPages = Math.max(1, Math.ceil((clicksQ.data?.total ?? 0) / (clicksQ.data?.perPage ?? 25)));

  const advertiserConfigs = advertiserQ.data?.postback_configs ?? [];
  const globalConfigs = advertiserQ.data?.global_postback_configs ?? [];
  const relevantPostbacks = [
    ...advertiserConfigs.map((c) => ({ ...c, scope: 'Advertiser' })),
    ...globalConfigs.map((c) => ({ ...c, scope: 'Global' })),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 rounded-xl p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold text-white">{campaign.name}</h2>
          <StatusPill active={campaign.is_active} />
          <span className="text-xs uppercase text-slate-400">{campaign.campaign_type}</span>
        </div>
        <div className="text-xs text-slate-400 mt-2 space-y-1">
          <div>Advertiser: <span className="text-slate-300">{campaign.source_name}</span></div>
          <div>Cost model: <span className="text-slate-300">{campaign.cost_model.toUpperCase()} · ${campaign.cost_value.toFixed(2)}{campaign.cpa_event ? ` on ${campaign.cpa_event}` : ''}</span></div>
          <div className="flex gap-3 flex-wrap">
            {campaign.utm_source && <span>utm_source=<span className="font-mono text-primary">{campaign.utm_source}</span></span>}
            {campaign.utm_campaign && <span>utm_campaign=<span className="font-mono text-primary">{campaign.utm_campaign}</span></span>}
            {campaign.aff_id && <span>aff_id=<span className="font-mono text-primary">{campaign.aff_id}</span></span>}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Clicks" value={stats.clicks} />
        <StatCard label="Signups" value={stats.signups} tone="info" />
        <StatCard label="Spend" value={`$${stats.total_cost.toFixed(2)}`} tone="bad" />
        <StatCard label="Avg CAC" value={`$${stats.avg_cac.toFixed(2)}`} />
        <StatCard label="ROI" value={`${stats.roi.toFixed(1)}%`} tone={stats.roi >= 0 ? 'good' : 'bad'} />
      </div>

      {/* Funnel */}
      <div className="bg-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-bold text-white mb-4">Funnel</h3>
        <div className="space-y-3">
          {funnel.map((s) => {
            const widthPct = funnel[0]?.count ? Math.max(4, (s.count / funnel[0].count) * 100) : 4;
            return (
              <div key={s.step} className="flex items-center gap-4">
                <div className="w-40 text-sm text-white font-medium">{s.step}</div>
                <div className="flex-1 bg-slate-700 rounded-full h-6 relative overflow-hidden">
                  <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${widthPct}%` }} />
                  <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-medium">
                    {s.count.toLocaleString()} {s.rate != null ? `(${s.rate}%)` : ''}
                  </span>
                </div>
              </div>
            );
          })}
          {funnel.length === 0 && <p className="text-xs text-slate-500">No funnel data yet.</p>}
        </div>
      </div>

      {/* Attributed clicks */}
      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Attributed clicks</h3>
          <span className="text-xs text-slate-400">{clicksQ.data?.total ?? 0} total</span>
        </div>
        {(clicksQ.data?.clicks.length ?? 0) === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No clicks attributed to this campaign yet.</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                  <th className="px-3 py-2">Session</th>
                  <th className="px-3 py-2">Member</th>
                  <th className="px-3 py-2">Aff ID</th>
                  <th className="px-3 py-2">Sub1</th>
                  <th className="px-3 py-2">Sub2</th>
                  <th className="px-3 py-2">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {(clicksQ.data?.clicks ?? []).map((c) => (
                  <tr key={c.id} className="hover:bg-slate-700/50">
                    <td className="px-3 py-2 text-slate-400 text-xs font-mono">{c.session_id.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-slate-300">{c.member?.email ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-300">{c.aff_id ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-300">{c.sub1 ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-300">{c.sub2 ?? '\u2014'}</td>
                    <td className="px-3 py-2 text-slate-400">{new Date(c.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex gap-2 justify-end p-3">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 rounded bg-slate-700 text-slate-400 text-sm disabled:opacity-40">Prev</button>
                <span className="px-3 py-1 text-slate-400 text-sm">{page}/{totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1 rounded bg-slate-700 text-slate-400 text-sm disabled:opacity-40">Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Postback triggers */}
      <div className="bg-slate-800 rounded-xl">
        <div className="px-5 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Postback triggers</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {campaign.postback_override
              ? <>Campaign has a postback override; advertiser/global configs still fire on matching events.</>
              : <>No override — uses advertiser-level and global configs matched at postback fire time.</>}
          </p>
        </div>
        {campaign.postback_override && (
          <div className="px-5 py-3 border-b border-slate-700">
            <p className="text-xs text-slate-400">Campaign override:</p>
            <p className="text-xs text-primary font-mono mt-1 break-all">{campaign.postback_override}</p>
          </div>
        )}
        {relevantPostbacks.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">No matching postback configs.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                <th className="px-3 py-2">Scope</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">URL</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {relevantPostbacks.map((p) => (
                <tr key={p.id}>
                  <td className="px-3 py-2 text-slate-400 text-xs">{p.scope}</td>
                  <td className="px-3 py-2 text-white">{p.name}</td>
                  <td className="px-3 py-2 text-primary text-xs">{p.event}</td>
                  <td className="px-3 py-2 text-slate-400 text-xs font-mono truncate max-w-[300px]">{p.url_template}</td>
                  <td className="px-3 py-2"><StatusPill active={p.is_active} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
